import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Box, Typography, Grid, Card, CardContent, Chip, Button, TextField, Alert, FormControl, InputLabel, Select, MenuItem, Checkbox, FormControlLabel, Paper } from '@mui/material';
import { XIcon, SendIcon } from 'lucide-react';
import { Icon } from '@iconify/react';
interface UnifiedTransactionModalProps {
  transaction: any;
  isOpen: boolean;
  onClose: () => void;
  mode?: 'view' | 'resubmit' | 'approve';
  onResubmit?: (reason: string, updatedData: any) => void;
  onApprove?: (decision: string, reason: string) => void;
  onDelete?: () => void;
}
const UnifiedTransactionModal: React.FC<UnifiedTransactionModalProps> = ({
  transaction,
  isOpen,
  onClose,
  mode = 'view',
  onResubmit,
  onApprove,
  onDelete
}) => {
  const [resubmitReason, setResubmitReason] = useState('');
  const [approvalDecision, setApprovalDecision] = useState('');
  const [approvalReason, setApprovalReason] = useState('');
  const [error, setError] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [editedData, setEditedData] = useState<any>({
    idType: transaction?.idType || 'CNIC',
    idNumber: transaction?.idNumber || '',
    veresysBioSys: transaction?.veresysBioSys || false,
    ofacUnscVerification: transaction?.ofacUnscVerification || false,
    contactNumber: transaction?.contactNumber || '',
    receiverCountry: transaction?.receiverCountry || 'Pakistan',
    province: transaction?.province || '',
    city: transaction?.city || '',
    idExpiry: transaction?.idExpiry || '',
    email: transaction?.email || '',
    dateOfBirth: transaction?.dateOfBirth || '',
    zipCode: transaction?.zipCode || '',
    address: transaction?.address || ''
  });
  const provinces = ['Punjab', 'Sindh', 'Khyber Pakhtunkhwa', 'Balochistan', 'Gilgit-Baltistan', 'Azad Kashmir'];
  const citiesByProvince: Record<string, string[]> = {
    Punjab: ['Lahore', 'Faisalabad', 'Rawalpindi', 'Multan', 'Gujranwala', 'Sialkot'],
    Sindh: ['Karachi', 'Hyderabad', 'Sukkur', 'Larkana'],
    'Khyber Pakhtunkhwa': ['Peshawar', 'Mardan', 'Abbottabad', 'Mingora'],
    Balochistan: ['Quetta', 'Turbat', 'Khuzdar', 'Hub'],
    'Gilgit-Baltistan': ['Gilgit', 'Skardu', 'Hunza'],
    'Azad Kashmir': ['Muzaffarabad', 'Mirpur', 'Rawalakot']
  };
  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2
    }).format(amount);
  };
  const validateAge = (dob: string): boolean => {
    const today = new Date();
    const birthDate = new Date(dob);
    const age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || monthDiff === 0 && today.getDate() < birthDate.getDate()) {
      return age - 1 >= 18;
    }
    return age >= 18;
  };
  const handleResubmit = () => {
    if (!resubmitReason.trim()) {
      setError('Resubmission reason is required');
      return;
    }
    // Validate ID verification fields
    if (!editedData.idType || !editedData.idNumber) {
      setError('ID Type and ID Number are required');
      return;
    }
    // Validate ID number format
    if (editedData.idType === 'CNIC' || editedData.idType === 'NICOP') {
      if (!/^\d{13}$/.test(editedData.idNumber)) {
        setError(`${editedData.idType} must be exactly 13 digits`);
        return;
      }
    } else if (editedData.idType === 'Passport') {
      if (!/^[A-Z0-9]{9}$/.test(editedData.idNumber.toUpperCase())) {
        setError('Passport number must be exactly 9 alphanumeric characters');
        return;
      }
    }
    // Validate contact number
    const contactRegex = /^\+92\d{10}$/;
    if (!contactRegex.test(editedData.contactNumber)) {
      setError('Contact number must be in format: +92XXXXXXXXXX');
      return;
    }
    // Validate other required fields
    if (!editedData.province || !editedData.city) {
      setError('Province and City are required');
      return;
    }
    if (!editedData.idExpiry) {
      setError('ID Expiry Date is required');
      return;
    }
    const expiryDate = new Date(editedData.idExpiry);
    const today = new Date();
    if (expiryDate <= today) {
      setError('ID Expiry Date must be in the future');
      return;
    }
    if (!editedData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editedData.email)) {
      setError('Valid email is required');
      return;
    }
    if (!editedData.dateOfBirth || !validateAge(editedData.dateOfBirth)) {
      setError('Age must be 18 years or above');
      return;
    }
    if (!editedData.zipCode || !/^\d{5}$/.test(editedData.zipCode)) {
      setError('Zip Code must be exactly 5 digits');
      return;
    }
    if (!editedData.address) {
      setError('Address is required');
      return;
    }
    if (onResubmit) {
      onResubmit(resubmitReason, editedData);
    }
    onClose();
  };
  const handleApprove = () => {
    if (!approvalDecision) {
      setError('Please select an approval decision');
      return;
    }
    if (!approvalReason.trim()) {
      setError('Reason is required');
      return;
    }
    if (onApprove) {
      onApprove(approvalDecision, approvalReason);
    }
    onClose();
  };
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Completed':
        return 'success';
      case 'Rejected':
        return 'error';
      case 'Pending':
      case 'Pending Checker Approval':
        return 'warning';
      case 'Processing':
      case 'In-Process':
        return 'info';
      default:
        return 'default';
    }
  };
  if (!transaction) return null;
  return <Dialog open={isOpen} onClose={onClose} maxWidth="md" fullWidth PaperProps={{
    sx: {
      borderRadius: 2,
      boxShadow: '0px 20px 25px rgba(0,0,0,0.15)',
      backgroundColor: '#FAFAFA',
      border: '1px solid #D1D5DB'
    }
  }} data-id="element-2437">
      <DialogTitle sx={{
      m: 0,
      p: 2.5,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      borderBottom: '1px solid #E5E7EB',
      backgroundColor: '#FAFAFA'
    }} data-id="element-2438">
        <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5
      }} data-id="element-2439">
          <Icon icon="lucide:file-text" width={24} height={24} color="#1E40AF" data-id="element-2440" />
          <Typography variant="h6" sx={{
          fontWeight: 600,
          color: '#374151'
        }} data-id="element-2441">
            Transaction Details
          </Typography>
        </Box>
        <IconButton aria-label="close" onClick={onClose} sx={{
        color: '#6B7280',
        '&:hover': {
          color: '#111827',
          backgroundColor: '#E5E7EB'
        }
      }} data-id="element-2442">
          <XIcon size={20} data-id="element-2443" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{
      p: 0,
      backgroundColor: '#F9FAFB'
    }} data-id="element-2444">
        <Grid container spacing={3} data-id="element-2445">
          {/* Rejection Reason Alert (if rejected) */}
          {transaction.rejectionReason && <Grid item xs={12} data-id="element-2446">
              <Alert severity="error" data-id="element-2447">
                <Typography variant="body2" fontWeight={600} gutterBottom data-id="element-2448">
                  Rejection Reason:
                </Typography>
                <Typography variant="body2" data-id="element-2449">
                  {transaction.rejectionReason}
                </Typography>
                {transaction.rejectedAt && <Typography variant="caption" color="text.secondary" sx={{
              mt: 1,
              display: 'block'
            }} data-id="element-2450">
                    Rejected on{' '}
                    {new Date(transaction.rejectedAt).toLocaleString()}
                  </Typography>}
              </Alert>
            </Grid>}

          {/* Sender Information */}
          <Grid item xs={12} md={6} lg={3} data-id="element-2451">
            <Card elevation={0} sx={{
            mb: 3,
            borderRadius: 2,
            border: '1px solid #D1D5DB',
            backgroundColor: '#FFFFFF',
            boxShadow: '0px 1px 2px rgba(0,0,0,0.05)'
          }} data-id="element-2452">
              <CardContent sx={{
              p: 3
            }} data-id="element-2453">
                <Typography variant="subtitle1" sx={{
                fontWeight: 600,
                mb: 3,
                color: '#1E3A8A',
                fontSize: '1.1rem'
              }} data-id="element-2454">
                  Sender Information
                </Typography>
                <Grid container spacing={2.5} data-id="element-2455">
                  <Grid item xs={12} data-id="element-2456">
                    <Typography variant="caption" color="text.secondary" sx={{
                    display: 'block',
                    mb: 0.5,
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }} data-id="element-2457">
                      Full Name
                    </Typography>
                    <Typography variant="body2" sx={{
                    fontWeight: 500
                  }} data-id="element-2458">
                      {transaction.senderName || transaction.parties?.sender?.name || 'Unknown'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} data-id="element-2459">
                    <Typography variant="caption" color="text.secondary" sx={{
                    display: 'block',
                    mb: 0.5,
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }} data-id="element-2460">
                      Country
                    </Typography>
                    <Typography variant="body2" sx={{
                    fontWeight: 500
                  }} data-id="element-2461">
                      {transaction.parties?.sender?.country || 'Unknown'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} data-id="element-2462">
                    <Typography variant="caption" color="text.secondary" sx={{
                    display: 'block',
                    mb: 0.5,
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }} data-id="element-2463">
                      City
                    </Typography>
                    <Typography variant="body2" sx={{
                    fontWeight: 500
                  }} data-id="element-2464">
                      {transaction.parties?.sender?.city || 'Unknown'}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Receiver Information */}
          <Grid item xs={12} md={6} lg={3} data-id="element-2465">
            <Card elevation={0} sx={{
            mb: 3,
            borderRadius: 2,
            border: '1px solid #D1D5DB',
            backgroundColor: '#FFFFFF',
            boxShadow: '0px 1px 2px rgba(0,0,0,0.05)'
          }} data-id="element-2466">
              <CardContent sx={{
              p: 3
            }} data-id="element-2467">
                <Typography variant="subtitle1" sx={{
                fontWeight: 600,
                mb: 3,
                color: '#1E3A8A',
                fontSize: '1.1rem'
              }} data-id="element-2468">
                  Receiver Information
                </Typography>
                <Grid container spacing={2.5} data-id="element-2469">
                  <Grid item xs={12} data-id="element-2470">
                    <Typography variant="caption" color="text.secondary" sx={{
                    display: 'block',
                    mb: 0.5,
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }} data-id="element-2471">
                      Full Name
                    </Typography>
                    <Typography variant="body2" sx={{
                    fontWeight: 500
                  }} data-id="element-2472">
                      {transaction.receiverName || transaction.parties?.receiver?.name || 'Unknown'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} data-id="element-2473">
                    <Typography variant="caption" color="text.secondary" sx={{
                    display: 'block',
                    mb: 0.5,
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }} data-id="element-2474">
                      Country
                    </Typography>
                    <Typography variant="body2" sx={{
                    fontWeight: 500
                  }} data-id="element-2475">
                      {transaction.parties?.receiver?.country || 'Pakistan'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} data-id="element-2476">
                    <Typography variant="caption" color="text.secondary" sx={{
                    display: 'block',
                    mb: 0.5,
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }} data-id="element-2477">
                      City
                    </Typography>
                    <Typography variant="body2" sx={{
                    fontWeight: 500
                  }} data-id="element-2478">
                      {transaction.parties?.receiver?.city || 'Unknown'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} data-id="element-2479">
                    <Typography variant="caption" color="text.secondary" sx={{
                    display: 'block',
                    mb: 0.5,
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }} data-id="element-2480">
                      Mobile Number
                    </Typography>
                    <Typography variant="body2" sx={{
                    fontWeight: 500
                  }} data-id="element-2481">
                      {transaction.parties?.receiver?.mobile || 'Unknown'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} data-id="element-2482">
                    <Typography variant="caption" color="text.secondary" sx={{
                    display: 'block',
                    mb: 0.5,
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }} data-id="element-2483">
                      Bank Name
                    </Typography>
                    <Typography variant="body2" sx={{
                    fontWeight: 500
                  }} data-id="element-2484">
                      {transaction.accounts?.bankName || 'Unknown'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} data-id="element-2485">
                    <Typography variant="caption" color="text.secondary" sx={{
                    display: 'block',
                    mb: 0.5,
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }} data-id="element-2486">
                      Bank Branch
                    </Typography>
                    <Typography variant="body2" sx={{
                    fontWeight: 500
                  }} data-id="element-2487">
                      {transaction.accounts?.bankBranch || 'Unknown'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} data-id="element-2488">
                    <Typography variant="caption" color="text.secondary" sx={{
                    display: 'block',
                    mb: 0.5,
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }} data-id="element-2489">
                      Account Number / IBAN
                    </Typography>
                    <Typography variant="body2" sx={{
                    fontWeight: 500
                  }} data-id="element-2490">
                      {transaction.accounts?.accountNumber || 'Unknown'}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Transaction Details */}
          <Grid item xs={12} md={6} lg={3} data-id="element-2491">
            <Card elevation={0} sx={{
            mb: 3,
            borderRadius: 2,
            border: '1px solid #D1D5DB',
            backgroundColor: '#FFFFFF',
            boxShadow: '0px 1px 2px rgba(0,0,0,0.05)'
          }} data-id="element-2492">
              <CardContent sx={{
              p: 3
            }} data-id="element-2493">
                <Typography variant="subtitle1" sx={{
                mb: 2,
                fontWeight: 600,
                color: '#374151'
              }} data-id="element-2494">
                  Transaction Details
                </Typography>
                <Grid container spacing={2.5} data-id="element-2495">
                  <Grid item xs={12} data-id="element-2496">
                    <Typography variant="caption" sx={{
                    color: '#6B7280',
                    fontWeight: 500
                  }} data-id="element-2497">
                      Transaction Date
                    </Typography>
                    <Typography variant="h5" sx={{
                    fontWeight: 700,
                    color: '#1E40AF'
                  }} data-id="element-2498">
                      {transaction.date || new Date(transaction.createdAt).toLocaleDateString()}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} data-id="element-2499">
                    <Typography variant="caption" sx={{
                    color: '#6B7280',
                    fontWeight: 500
                  }} data-id="element-2500">
                      Amount
                    </Typography>
                    <Typography variant="h5" sx={{
                    fontWeight: 700,
                    color: '#1E40AF'
                  }} data-id="element-2501">
                      {transaction.currency}{' '}
                      {transaction.amount.toLocaleString()}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} data-id="element-2502">
                    <Typography variant="caption" sx={{
                    color: '#6B7280',
                    fontWeight: 500
                  }} data-id="element-2503">
                      Exchange Rate
                    </Typography>
                    <Typography variant="body2" sx={{
                    fontWeight: 500
                  }} data-id="element-2504">
                      {transaction.overview?.exchangeRate || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} data-id="element-2505">
                    <Typography variant="caption" sx={{
                    color: '#6B7280',
                    fontWeight: 500
                  }} data-id="element-2506">
                      Payout Amount
                    </Typography>
                    <Typography variant="h5" sx={{
                    fontWeight: 700,
                    color: '#0B7923'
                  }} data-id="element-2507">
                      {transaction.payoutCurrency}{' '}
                      {transaction.payoutAmount.toLocaleString()}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} data-id="element-2508">
                    <Typography variant="caption" color="text.secondary" sx={{
                    display: 'block',
                    mb: 0.5,
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }} data-id="element-2509">
                      Purpose of Remittance
                    </Typography>
                    <Typography variant="body2" sx={{
                    fontWeight: 500
                  }} data-id="element-2510">
                      {transaction.amounts?.purpose || 'Family Support'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} data-id="element-2511">
                    <Typography variant="caption" color="text.secondary" sx={{
                    display: 'block',
                    mb: 0.5,
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }} data-id="element-2512">
                      Relationship with Receiver
                    </Typography>
                    <Typography variant="body2" sx={{
                    fontWeight: 500
                  }} data-id="element-2513">
                      {transaction.relationship || 'Family Member'}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Teller Information */}
          <Grid item xs={12} md={6} lg={3} data-id="element-2514">
            <Card elevation={0} sx={{
            mb: 3,
            borderRadius: 2,
            border: '1px solid #D1D5DB',
            backgroundColor: '#FFFFFF',
            boxShadow: '0px 1px 2px rgba(0,0,0,0.05)'
          }} data-id="element-2515">
              <CardContent sx={{
              p: 3
            }} data-id="element-2516">
                <Typography variant="subtitle1" sx={{
                fontWeight: 600,
                mb: 3,
                color: '#1E3A8A',
                fontSize: '1.1rem'
              }} data-id="element-2517">
                  Teller Information
                </Typography>
                <Grid container spacing={2.5} data-id="element-2518">
                  <Grid item xs={12} data-id="element-2519">
                    <Typography variant="caption" color="text.secondary" sx={{
                    display: 'block',
                    mb: 0.5,
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }} data-id="element-2520">
                      Branch Code
                    </Typography>
                    <Typography variant="body2" sx={{
                    fontWeight: 500
                  }} data-id="element-2521">
                      {transaction.branchCode}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} data-id="element-2522">
                    <Typography variant="caption" color="text.secondary" sx={{
                    display: 'block',
                    mb: 0.5,
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }} data-id="element-2523">
                      Branch Name
                    </Typography>
                    <Typography variant="body2" sx={{
                    fontWeight: 500
                  }} data-id="element-2524">
                      Main Branch
                    </Typography>
                  </Grid>
                  <Grid item xs={12} data-id="element-2525">
                    <Typography variant="caption" color="text.secondary" sx={{
                    display: 'block',
                    mb: 0.5,
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }} data-id="element-2526">
                      Payment Date
                    </Typography>
                    <Typography variant="body2" sx={{
                    fontWeight: 500
                  }} data-id="element-2527">
                      {transaction.date || new Date(transaction.createdAt).toLocaleDateString()}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} data-id="element-2528">
                    <Typography variant="caption" color="text.secondary" sx={{
                    display: 'block',
                    mb: 0.5,
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }} data-id="element-2529">
                      Teller ID
                    </Typography>
                    <Typography variant="body2" sx={{
                    fontWeight: 500
                  }} data-id="element-2530">
                      TL001
                    </Typography>
                  </Grid>
                  <Grid item xs={12} data-id="element-2531">
                    <Typography variant="caption" color="text.secondary" sx={{
                    display: 'block',
                    mb: 0.5,
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }} data-id="element-2532">
                      Teller Name
                    </Typography>
                    <Typography variant="body2" sx={{
                    fontWeight: 500
                  }} data-id="element-2533">
                      Branch Maker
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* ID Verification Details */}
          <Grid item xs={12} data-id="element-2534">
            <Paper sx={{
            p: 2,
            bgcolor: mode === 'resubmit' || mode === 'approve' ? 'primary.50' : 'grey.50'
          }} variant="outlined" data-id="element-2535">
              <Typography variant="subtitle1" sx={{
              mb: 2,
              fontWeight: 'bold',
              color: mode === 'resubmit' || mode === 'approve' ? 'primary.main' : 'text.primary'
            }} data-id="element-2536">
                ID Verification Details{' '}
                {(mode === 'resubmit' || mode === 'approve') && '(Editable)'}
              </Typography>
              {error && (mode === 'resubmit' || mode === 'approve') && <Alert severity="error" sx={{
              mb: 2
            }} data-id="element-2537">
                  {error}
                </Alert>}
              <Grid container spacing={2} data-id="element-2538">
                {mode === 'resubmit' || mode === 'approve' ? <>
                    <Grid item xs={12} sm={6} data-id="element-2539">
                      <FormControl fullWidth size="small" required data-id="element-2540">
                        <InputLabel data-id="element-2541">ID Type</InputLabel>
                        <Select value={editedData.idType} label="ID Type" onChange={e => {
                      setEditedData({
                        ...editedData,
                        idType: e.target.value,
                        idNumber: ''
                      });
                    }} data-id="element-2542">
                          <MenuItem value="CNIC" data-id="element-2543">CNIC</MenuItem>
                          <MenuItem value="NICOP" data-id="element-2544">NICOP</MenuItem>
                          <MenuItem value="Passport" data-id="element-2545">Passport</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} data-id="element-2546">
                      <TextField fullWidth required label="ID Number" value={editedData.idNumber} onChange={e => {
                    const value = e.target.value;
                    if (editedData.idType === 'Passport') {
                      setEditedData({
                        ...editedData,
                        idNumber: value.toUpperCase()
                      });
                    } else {
                      setEditedData({
                        ...editedData,
                        idNumber: value.replace(/\D/g, '')
                      });
                    }
                  }} placeholder={editedData.idType === 'Passport' ? 'Enter 9 alphanumeric characters' : 'Enter 13 digits'} helperText={editedData.idType === 'Passport' ? '9 alphanumeric characters' : '13 digits'} size="small" inputProps={{
                    maxLength: editedData.idType === 'Passport' ? 9 : 13
                  }} data-id="element-2547" />
                    </Grid>
                    <Grid item xs={12} data-id="element-2548">
                      <Box sx={{
                    display: 'flex',
                    gap: 2,
                    flexWrap: 'wrap'
                  }} data-id="element-2549">
                        <FormControlLabel control={<Checkbox checked={editedData.veresysBioSys} onChange={e => setEditedData({
                      ...editedData,
                      veresysBioSys: e.target.checked
                    })} data-id="element-2551" />} label="Veresys (Bio-Sys)" data-id="element-2550" />
                        <FormControlLabel control={<Checkbox checked={editedData.ofacUnscVerification} onChange={e => setEditedData({
                      ...editedData,
                      ofacUnscVerification: e.target.checked
                    })} data-id="element-2553" />} label="OFAC/UNSC Verification" data-id="element-2552" />
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} data-id="element-2554">
                      <TextField fullWidth required label="Contact Number" value={editedData.contactNumber} onChange={e => {
                    const value = e.target.value;
                    if (value.startsWith('+92')) {
                      setEditedData({
                        ...editedData,
                        contactNumber: value.slice(0, 13)
                      });
                    } else if (value === '+' || value === '+9') {
                      setEditedData({
                        ...editedData,
                        contactNumber: value
                      });
                    } else if (!value.startsWith('+92') && value.length > 0) {
                      setEditedData({
                        ...editedData,
                        contactNumber: '+92'
                      });
                    } else {
                      setEditedData({
                        ...editedData,
                        contactNumber: value
                      });
                    }
                  }} placeholder="+92XXXXXXXXXX" helperText="+92 followed by 10 digits" size="small" data-id="element-2555" />
                    </Grid>
                    <Grid item xs={12} sm={6} data-id="element-2556">
                      <TextField fullWidth disabled label="Country" value={editedData.receiverCountry} size="small" helperText="Pakistan only" data-id="element-2557" />
                    </Grid>
                    <Grid item xs={12} sm={6} data-id="element-2558">
                      <FormControl fullWidth size="small" required data-id="element-2559">
                        <InputLabel data-id="element-2560">Province</InputLabel>
                        <Select value={editedData.province} label="Province" onChange={e => {
                      setEditedData({
                        ...editedData,
                        province: e.target.value,
                        city: ''
                      });
                    }} data-id="element-2561">
                          {provinces.map(p => <MenuItem key={p} value={p} data-id="element-2562">
                              {p}
                            </MenuItem>)}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} data-id="element-2563">
                      <FormControl fullWidth size="small" required disabled={!editedData.province} data-id="element-2564">
                        <InputLabel data-id="element-2565">City</InputLabel>
                        <Select value={editedData.city} label="City" onChange={e => setEditedData({
                      ...editedData,
                      city: e.target.value
                    })} data-id="element-2566">
                          {editedData.province && citiesByProvince[editedData.province]?.map(c => <MenuItem key={c} value={c} data-id="element-2567">
                                {c}
                              </MenuItem>)}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} data-id="element-2568">
                      <TextField fullWidth required label="ID Expiry Date" type="date" value={editedData.idExpiry} onChange={e => setEditedData({
                    ...editedData,
                    idExpiry: e.target.value
                  })} InputLabelProps={{
                    shrink: true
                  }} inputProps={{
                    min: new Date().toISOString().split('T')[0]
                  }} helperText="Must be a future date" size="small" data-id="element-2569" />
                    </Grid>
                    <Grid item xs={12} sm={6} data-id="element-2570">
                      <TextField fullWidth required label="Email" type="email" value={editedData.email} onChange={e => setEditedData({
                    ...editedData,
                    email: e.target.value
                  })} placeholder="example@email.com" size="small" data-id="element-2571" />
                    </Grid>
                    <Grid item xs={12} sm={6} data-id="element-2572">
                      <TextField fullWidth required label="Date of Birth" type="date" value={editedData.dateOfBirth} onChange={e => setEditedData({
                    ...editedData,
                    dateOfBirth: e.target.value
                  })} InputLabelProps={{
                    shrink: true
                  }} inputProps={{
                    max: new Date().toISOString().split('T')[0]
                  }} helperText="Must be 18 years or older" size="small" data-id="element-2573" />
                    </Grid>
                    <Grid item xs={12} sm={6} data-id="element-2574">
                      <TextField fullWidth required label="Zip Code" value={editedData.zipCode} onChange={e => {
                    const value = e.target.value.replace(/\D/g, '');
                    setEditedData({
                      ...editedData,
                      zipCode: value.slice(0, 5)
                    });
                  }} placeholder="Enter 5 digits" helperText="5 digits" size="small" inputProps={{
                    maxLength: 5
                  }} data-id="element-2575" />
                    </Grid>
                    <Grid item xs={12} data-id="element-2576">
                      <TextField fullWidth required label="Address" value={editedData.address} onChange={e => setEditedData({
                    ...editedData,
                    address: e.target.value
                  })} multiline rows={3} placeholder="Enter complete address" size="small" data-id="element-2577" />
                    </Grid>
                  </> : <>
                    <Grid item xs={12} sm={6} md={4} data-id="element-2578">
                      <Typography variant="caption" color="text.secondary" data-id="element-2579">
                        Receiver Name
                      </Typography>
                      <Typography variant="body2" fontWeight={500} data-id="element-2580">
                        {transaction.receiverName || transaction.parties?.receiver?.name || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} data-id="element-2581">
                      <Typography variant="caption" color="text.secondary" data-id="element-2582">
                        ID Type
                      </Typography>
                      <Typography variant="body2" fontWeight={500} data-id="element-2583">
                        {transaction.idType || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} data-id="element-2584">
                      <Typography variant="caption" color="text.secondary" data-id="element-2585">
                        ID Number
                      </Typography>
                      <Typography variant="body2" fontWeight={500} data-id="element-2586">
                        {transaction.idNumber || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} data-id="element-2587">
                      <Typography variant="caption" color="text.secondary" data-id="element-2588">
                        Contact Number
                      </Typography>
                      <Typography variant="body2" fontWeight={500} data-id="element-2589">
                        {transaction.contactNumber || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} data-id="element-2590">
                      <Typography variant="caption" color="text.secondary" data-id="element-2591">
                        Country
                      </Typography>
                      <Typography variant="body2" fontWeight={500} data-id="element-2592">
                        {transaction.receiverCountry || 'Pakistan'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} data-id="element-2593">
                      <Typography variant="caption" color="text.secondary" data-id="element-2594">
                        Province
                      </Typography>
                      <Typography variant="body2" fontWeight={500} data-id="element-2595">
                        {transaction.province || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} data-id="element-2596">
                      <Typography variant="caption" color="text.secondary" data-id="element-2597">
                        City
                      </Typography>
                      <Typography variant="body2" fontWeight={500} data-id="element-2598">
                        {transaction.city || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} data-id="element-2599">
                      <Typography variant="caption" color="text.secondary" data-id="element-2600">
                        ID Expiry Date
                      </Typography>
                      <Typography variant="body2" fontWeight={500} data-id="element-2601">
                        {transaction.idExpiry || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} data-id="element-2602">
                      <Typography variant="caption" color="text.secondary" data-id="element-2603">
                        Email
                      </Typography>
                      <Typography variant="body2" fontWeight={500} data-id="element-2604">
                        {transaction.email || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} data-id="element-2605">
                      <Typography variant="caption" color="text.secondary" data-id="element-2606">
                        Date of Birth
                      </Typography>
                      <Typography variant="body2" fontWeight={500} data-id="element-2607">
                        {transaction.dateOfBirth || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} data-id="element-2608">
                      <Typography variant="caption" color="text.secondary" data-id="element-2609">
                        Zip Code
                      </Typography>
                      <Typography variant="body2" fontWeight={500} data-id="element-2610">
                        {transaction.zipCode || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} data-id="element-2611">
                      <Typography variant="caption" color="text.secondary" data-id="element-2612">
                        Address
                      </Typography>
                      <Typography variant="body2" fontWeight={500} data-id="element-2613">
                        {transaction.address || 'N/A'}
                      </Typography>
                    </Grid>
                  </>}
              </Grid>
            </Paper>
          </Grid>

          {/* Resubmission Reason (only in resubmit mode) */}
          {mode === 'resubmit' && <Grid item xs={12} data-id="element-2614">
              <Card elevation={0} sx={{
            bgcolor: 'warning.50',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'warning.light'
          }} data-id="element-2615">
                <CardContent data-id="element-2616">
                  <Typography variant="h6" sx={{
                mb: 2,
                fontWeight: 600,
                color: 'warning.main'
              }} data-id="element-2617">
                    Resubmission Reason *
                  </Typography>
                  <TextField fullWidth required label="Reason for Resubmission" multiline rows={3} value={resubmitReason} onChange={e => setResubmitReason(e.target.value)} placeholder="Explain what changes were made and why this transaction should be reconsidered" helperText="This reason will be visible to the checker" data-id="element-2618" />
                </CardContent>
              </Card>
            </Grid>}

          {/* Approval Decision (only in approve mode) */}
          {mode === 'approve' && <Grid item xs={12} data-id="element-2619">
              <Card elevation={0} sx={{
            border: '2px solid #1E40AF',
            borderRadius: 2,
            bgcolor: 'rgba(30, 64, 175, 0.05)'
          }} data-id="element-2620">
                <CardContent data-id="element-2621">
                  <Typography variant="h6" sx={{
                mb: 2,
                color: '#1E40AF',
                fontWeight: 600
              }} data-id="element-2622">
                    Approval Decision
                  </Typography>
                  {error && <Alert severity="error" sx={{
                mb: 2
              }} data-id="element-2623">
                      {error}
                    </Alert>}
                  <Grid container spacing={2} data-id="element-2624">
                    <Grid item xs={12} data-id="element-2625">
                      <FormControl component="fieldset" required data-id="element-2626">
                        <Box sx={{
                      display: 'flex',
                      gap: 2
                    }} data-id="element-2627">
                          <Button variant={approvalDecision === 'completed' ? 'contained' : 'outlined'} color="success" onClick={() => setApprovalDecision('completed')} data-id="element-2628">
                            Approve Transaction
                          </Button>
                          <Button variant={approvalDecision === 'rejected' ? 'contained' : 'outlined'} color="error" onClick={() => setApprovalDecision('rejected')} data-id="element-2629">
                            Reject Transaction
                          </Button>
                        </Box>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} data-id="element-2630">
                      <TextField fullWidth required label="Reason" multiline rows={3} value={approvalReason} onChange={e => setApprovalReason(e.target.value)} placeholder="Enter reason for approval or rejection" data-id="element-2631" />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>}
        </Grid>
      </DialogContent>

      <DialogActions sx={{
      p: 2.5,
      borderTop: '1px solid #D1D5DB',
      backgroundColor: '#FAFAFA'
    }} data-id="element-2632">
        <Button onClick={onClose} variant="outlined" sx={{
        color: '#6B7280',
        borderColor: '#D1D5DB',
        '&:hover': {
          backgroundColor: '#F3F4F6',
          borderColor: '#9CA3AF'
        }
      }} data-id="element-2633">
          Close
        </Button>
        <Box sx={{
        display: 'flex',
        gap: 1
      }} data-id="element-2634">
          {mode === 'resubmit' && onDelete && <Button onClick={() => setShowDeleteConfirm(true)} variant="contained" color="error" sx={{
          backgroundColor: '#B91C1C',
          '&:hover': {
            backgroundColor: '#7F1D1D'
          }
        }} data-id="element-2635">
              Delete Transaction
            </Button>}
          {mode === 'resubmit' && <Button onClick={handleResubmit} variant="contained" color="primary" disabled={!resubmitReason} startIcon={<SendIcon size={16} data-id="element-2637" />} sx={{
          backgroundColor: '#1E40AF',
          '&:hover': {
            backgroundColor: '#1C3AA9'
          }
        }} data-id="element-2636">
              Resubmit
            </Button>}
          {mode === 'approve' && <Button onClick={handleApprove} variant="contained" color="primary" disabled={!approvalDecision || !approvalReason} sx={{
          backgroundColor: '#1E40AF',
          '&:hover': {
            backgroundColor: '#1C3AA9'
          }
        }} data-id="element-2638">
              Submit Decision
            </Button>}
        </Box>
      </DialogActions>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onClose={() => setShowDeleteConfirm(false)} maxWidth="sm" fullWidth data-id="element-2639">
        <DialogTitle data-id="element-2640">
          <Typography variant="h6" color="error.main" data-id="element-2641">
            Delete Transaction
          </Typography>
        </DialogTitle>
        <DialogContent data-id="element-2642">
          <Alert severity="warning" sx={{
          mb: 2
        }} data-id="element-2643">
            This action cannot be undone!
          </Alert>
          <Typography variant="body2" color="text.secondary" data-id="element-2644">
            Are you sure you want to delete transaction{' '}
            <strong data-id="element-2645">{transaction?.id}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{
          mt: 2
        }} data-id="element-2646">
            This will permanently remove the transaction from the system.
          </Typography>
        </DialogContent>
        <DialogActions sx={{
        p: 2
      }} data-id="element-2647">
          <Button onClick={() => setShowDeleteConfirm(false)} data-id="element-2648">Cancel</Button>
          <Button variant="contained" color="error" onClick={() => {
          setShowDeleteConfirm(false);
          onClose();
          if (onDelete) onDelete();
        }} data-id="element-2649">
            Delete Transaction
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>;
};
export default UnifiedTransactionModal;